import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const CandidateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Mock data - replace with actual API call
  const candidate = {
    id: 1,
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 123-4567',
    location: 'New York, NY',
    dateOfBirth: '1990-05-15',
    gender: 'Female',
    currentTitle: 'Senior Software Engineer',
    currentCompany: 'TechCorp Inc.',
    experience: '8 years',
    education: "Master's Degree",
    skills: ['JavaScript', 'React', 'Node.js', 'Python', 'AWS', 'Docker'],
    desiredRoles: ['Senior Developer', 'Tech Lead', 'Full Stack Developer'],
    preferredLocations: ['New York', 'Remote', 'San Francisco'],
    salaryExpectation: '$120,000 - $150,000',
    workType: 'full-time',
    status: 'active',
    matchScore: 92,
    appliedJobs: 12,
    interviews: 3,
    lastActive: '2024-01-15',
    joinDate: '2023-06-10',
    notes: 'Highly skilled candidate with excellent communication skills. Strong background in full-stack development.',
    resume: 'sarah_johnson_resume.pdf'
  };

  const applications = [
    { id: 1, jobTitle: 'Senior Frontend Developer', company: 'TechStart Inc.', appliedDate: '2024-01-10', status: 'Under Review', match: 95 },
    { id: 2, jobTitle: 'Full Stack Engineer', company: 'Digital Solutions', appliedDate: '2024-01-08', status: 'Interview', match: 88 },
    { id: 3, jobTitle: 'React Developer', company: 'WebTech LLC', appliedDate: '2024-01-05', status: 'Rejected', match: 76 },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Under Review': { color: 'bg-yellow-100 text-yellow-800', icon: 'fas fa-clock' },
      'Interview': { color: 'bg-blue-100 text-blue-800', icon: 'fas fa-calendar' },
      'Rejected': { color: 'bg-red-100 text-red-800', icon: 'fas fa-times' },
      'Accepted': { color: 'bg-green-100 text-green-800', icon: 'fas fa-check' }
    };
    const config = statusConfig[status] || statusConfig['Under Review'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <i className={`${config.icon} mr-1`}></i>
        {status}
      </span>
    );
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Candidate Profile | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/hpanel/candidates')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="fas fa-arrow-left text-xl"></i>
            </button>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {candidate.firstName} {candidate.lastName}
              </h2>
              <p className="text-gray-400">{candidate.currentTitle} at {candidate.currentCompany}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/hpanel/candidates/edit/${candidate.id}`}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
            >
              <i className="fas fa-edit mr-2"></i>
              Edit Profile
            </Link>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center">
              <i className="fas fa-download mr-2"></i>
              Download Resume
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-gray-900">{candidate.firstName} {candidate.lastName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-gray-900">{candidate.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="text-gray-900">{candidate.phone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Location</label>
                <p className="text-gray-900">{candidate.location}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                <p className="text-gray-900">{new Date(candidate.dateOfBirth).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Gender</label>
                <p className="text-gray-900">{candidate.gender}</p>
              </div>
            </div>
          </div>

          {/* Professional Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Professional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Current Title</label>
                <p className="text-gray-900">{candidate.currentTitle}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Current Company</label>
                <p className="text-gray-900">{candidate.currentCompany}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Experience</label>
                <p className="text-gray-900">{candidate.experience}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Education</label>
                <p className="text-gray-900">{candidate.education}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-500 mb-2 block">Skills</label>
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">Desired Roles</label>
              <div className="flex flex-wrap gap-2">
                {candidate.desiredRoles.map((role, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Job Applications Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Job Applications</h3>
            <div className="space-y-4">
              {applications.map((application) => (
                <div key={application.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{application.jobTitle}</h4>
                    <p className="text-sm text-gray-600">{application.company}</p>
                    <p className="text-xs text-gray-500">Applied on {new Date(application.appliedDate).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(application.status)}
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchScoreColor(application.match)}`}>
                      <i className="fas fa-robot mr-1"></i>
                      {application.match}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 text-center text-primary-600 text-sm font-medium py-2 hover:bg-gray-50 rounded-lg">
              View All Applications
            </button>
          </div>
        </div>

        {/* Right Column - Stats & Actions */}
        <div className="space-y-6">
          {/* Profile Summary Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-2xl mx-auto mb-4">
                {candidate.firstName.charAt(0)}{candidate.lastName.charAt(0)}
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{candidate.firstName} {candidate.lastName}</h3>
              <p className="text-gray-400">{candidate.currentTitle}</p>
              
              <div className="mt-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">AI Match Score</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchScoreColor(candidate.matchScore)}`}>
                    {candidate.matchScore}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {candidate.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Applied Jobs</span>
                  <span className="text-sm font-medium text-gray-900">{candidate.appliedJobs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Interviews</span>
                  <span className="text-sm font-medium text-gray-900">{candidate.interviews}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Member Since</span>
                  <span className="text-sm text-gray-900">{new Date(candidate.joinDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-envelope mr-3 text-gray-400"></i>
                Send Message
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-calendar mr-3 text-gray-400"></i>
                Schedule Interview
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-file-alt mr-3 text-gray-400"></i>
                Generate Report
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center">
                <i className="fas fa-archive mr-3"></i>
                Archive Candidate
              </button>
            </div>
          </div>

          {/* AI Insights Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">AI Insights</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <i className="fas fa-lightbulb text-yellow-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  High match score indicates strong fit for senior technical roles.
                </p>
              </div>
              <div className="flex items-start">
                <i className="fas fa-chart-line text-green-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  Skills align well with current market demands for full-stack developers.
                </p>
              </div>
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-orange-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  Consider suggesting roles with leadership opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateView;